import React, { FormEvent } from "react";
import { Button, Input, Textarea, Tooltip } from "@heroui/react";
import {
  ArrowLeftIcon,
  GlobeAltIcon,
  UserIcon,
  InformationCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { State, ValidationErrors } from "../../types/types";
import { TFunction } from "react-i18next";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const inputClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const textareaClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

interface SponsorStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  validationErrors: ValidationErrors;
  addSponsorLink: () => void;
  updateSponsorLink: (
    index: number,
    field: "name" | "description" | "link",
    value: string,
  ) => void;
  removeSponsorLink: (index: number) => void;
  handleSponsorLogoUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => void;
  isValidURL: (url: string) => boolean;
  handleFormSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleBack: () => void;
  isSubmitting: boolean;
}

const SponsorStage: React.FC<SponsorStageProps> = ({
  t,
  state,
  validationErrors,
  addSponsorLink,
  updateSponsorLink,
  removeSponsorLink,
  handleSponsorLogoUpload,
  isValidURL,
  handleFormSubmit,
  handleBack,
  isSubmitting,
}) => {
  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                <span className="inline-flex items-center gap-2">
                  {t("sponsor.title")}
                  <Tooltip
                    className="w-[300px]"
                    placement="right"
                    content={
                      <div className="text-xs p-1">
                        <span className="font-semibold block mb-0.5">
                          {t("sponsor.title")}
                        </span>
                        <span>{t("sponsor.subtitle")}</span>
                      </div>
                    }
                  >
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </span>
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("sponsor.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={handleFormSubmit}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                <div className="space-y-3">
                  <h3 className="text-xs sm:text-sm font-semibold lg:font-medium text-slate-700 lg:text-[#18181B]">
                    {t("sponsor.sectionTitle")}
                  </h3>

                  {state.formData.fighterDto?.fighterSponsorsRequestDtos?.map(
                    (sponsor, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-3 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#F9FAFB] p-3.5"
                      >
                        <Input
                          type="text"
                          name={`sponsorName-${index}`}
                          label={t("sponsor.nameLabel")}
                          labelPlacement="outside"
                          placeholder={t("sponsor.namePlaceholder")}
                          value={sponsor.name}
                          onValueChange={(value) =>
                            updateSponsorLink(index, "name", value)
                          }
                          isRequired
                          maxLength={50}
                          isInvalid={
                            !!validationErrors[
                              `fighterDto.fighterSponsorsRequestDtos.${index}.name`
                            ]
                          }
                          errorMessage={
                            validationErrors[
                              `fighterDto.fighterSponsorsRequestDtos.${index}.name`
                            ]
                          }
                          className="w-full"
                          classNames={inputClassNames}
                          startContent={
                            <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                          }
                        />

                        <Textarea
                          name={`sponsorDescription-${index}`}
                          label={t("sponsor.descriptionLabel")}
                          labelPlacement="outside"
                          placeholder={t("sponsor.descriptionPlaceholder")}
                          value={sponsor.description}
                          onValueChange={(value) =>
                            updateSponsorLink(index, "description", value)
                          }
                          maxLength={200}
                          isInvalid={
                            !!validationErrors[
                              `fighterDto.fighterSponsorsRequestDtos.${index}.description`
                            ]
                          }
                          errorMessage={
                            validationErrors[
                              `fighterDto.fighterSponsorsRequestDtos.${index}.description`
                            ]
                          }
                          className="w-full"
                          classNames={textareaClassNames}
                        />

                        <Input
                          type="url"
                          name={`sponsorCompanyUrl-${index}`}
                          label={t("sponsor.companyUrl")}
                          labelPlacement="outside"
                          placeholder={t("sponsor.companyUrlPlaceholder")}
                          value={sponsor.link}
                          onValueChange={(value) =>
                            updateSponsorLink(index, "link", value)
                          }
                          errorMessage={
                            validationErrors[
                              `fighterDto.fighterSponsorsRequestDtos.${index}.link`
                            ] ||
                            (sponsor.link && !isValidURL(sponsor.link)
                              ? t("sponsor.invalidUrl")
                              : undefined)
                          }
                          className="w-full"
                          classNames={inputClassNames}
                          startContent={
                            <GlobeAltIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                          }
                        />

                        {/* Logo upload */}
                        <div className="border-2 border-dashed border-slate-200 lg:border-[#EBEBEC] bg-white hover:bg-slate-50 h-28 flex items-center justify-center rounded-xl relative transition-all">
                          <label
                            htmlFor={`sponsorLogoFile-${index}`}
                            className="cursor-pointer flex flex-col items-center p-3 text-center"
                          >
                            <svg
                              className="w-6 h-6 text-slate-400 mb-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.8}
                                d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                              />
                            </svg>
                            <span className="text-xs font-semibold text-[#18181B]">
                              {t("sponsor.uploadLogo")}
                            </span>
                            <span className="text-[10px] text-[#71717A]">
                              {t("sponsor.logoDescription")}
                            </span>
                            <input
                              id={`sponsorLogoFile-${index}`}
                              name={`sponsorLogoFile-${index}`}
                              type="file"
                              accept="image/png,image/jpeg,image/jpg"
                              onChange={(event) =>
                                handleSponsorLogoUpload(event, index)
                              }
                              className="hidden"
                            />
                          </label>
                          {sponsor.logo && (
                            <div className="absolute top-2 right-2">
                              <img
                                src={sponsor.logo}
                                alt={`Sponsor Logo ${index + 1}`}
                                className="h-12 w-12 rounded-lg object-cover border border-slate-200 shadow-xs"
                                onError={(e) => {
                                  e.currentTarget.src = "/default-avatar.png";
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <Button
                          onPress={() => removeSponsorLink(index)}
                          color="danger"
                          variant="flat"
                          size="sm"
                          className="self-end rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium px-3 py-1.5 transition-all"
                        >
                          {t("sponsor.removeSponsor")}
                        </Button>
                      </div>
                    )
                  )}

                  <Button
                    onPress={addSponsorLink}
                    variant="bordered"
                    disabled={
                      (state.formData.fighterDto?.fighterSponsorsRequestDtos?.length ?? 0) >= 5
                    }
                    className="w-full rounded-xl border border-[#0485F7] text-[#0485F7] text-xs sm:text-sm font-semibold h-9 hover:bg-[#0485F7]/10 transition-all disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                  >
                    {(state.formData.fighterDto?.fighterSponsorsRequestDtos?.length ?? 0) >= 5
                      ? t("sponsor.sponsorLimitReached")
                      : t("sponsor.addSponsor")}
                  </Button>
                </div>
              </div>

              {/* Footer: Back / Submit */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  endContent={!isSubmitting && <ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isSubmitting ? t("navigation.submitting") : t("navigation.submit")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default SponsorStage;
